/**
 * config.js — Tenant Configuration Loader
 *
 * PURPOSE:
 *   Fetches and caches tenant-specific configuration from the backend.
 *   Provides config to all other modules.
 *
 * HOW IT WORKS:
 *   loadTenantConfig() calls the config API, caches in sessionStorage.
 *   fetchTenantConfig() resolves tenant ID and delegates.
 *
 * BE DEPENDENCY:
 *   GET /api/v1/tenants/{tenant_id}/config
 *
 * USAGE:
 *   import { fetchTenantConfig } from './core/config.js';
 *   const config = await fetchTenantConfig(tenantId);
 */

import { API__BASE_URL, APP_ENV, getTenantId, log, warn, error } from "./env.js";
import { normalizeTenantConfig } from "./normalize.js";

const CONFIG_CACHE_PREFIX = "ws_config_";

export { getTenantId };

export async function fetchTenantConfig(tenantId) {
  const resolvedTenantId = tenantId || getTenantId();
  return loadTenantConfig(resolvedTenantId);
}

export async function loadTenantConfig(tenantId) {
  const resolvedTenantId = tenantId || getTenantId();
  if (!resolvedTenantId && APP_ENV === "production") {
    warn("No tenant ID found - using production config");
  }

  const cacheKey = `${CONFIG_CACHE_PREFIX}${resolvedTenantId || 'production'}`;
  // Cache only in prod. Dev iteration on admin-saved config is faster
  // with no cache; admin edits become visible on plain reload.
  const useCache = APP_ENV !== "development";
  if (useCache) {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        warn("Failed to parse cached config, fetching fresh", e);
      }
    }
  }

  try {
    const url = resolvedTenantId
      ? `${API__BASE_URL}/api/v1/tenant-config/${resolvedTenantId}/`
      : `${API__BASE_URL}/api/v1/tenant-config/`;
    // Bypass the browser HTTP cache: the BE sends no Cache-Control, so a plain
    // fetch() gets heuristically cached and the widget would keep serving a
    // stale config after admin edits. (Prod still has its own sessionStorage
    // cache below, which a new session clears.)
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const body = await response.json();
    const payload = (body && typeof body === "object" && "data" in body) ? body.data : body;
    const config = normalizeTenantConfig(payload);
    if (useCache) sessionStorage.setItem(cacheKey, JSON.stringify(config));
    log("Loaded config for tenant:", resolvedTenantId || 'production');
    return config;
  } catch (e) {
    error("Failed to load tenant config, using defaults", e);
    return normalizeTenantConfig({
      tenant_id: resolvedTenantId || "production",
    });
  }
}